import React, { useState } from 'react';
import {
    View,
    Text,
    ScrollView,
    Image,
    TouchableOpacity,
    StyleSheet,
    Dimensions,
    TextInput,
    SafeAreaView,
    Platform,
    Alert
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useCart } from '../../context/CartContext';
import MarketplaceFooter from '../../components/common/MarketplaceFooter';
import { getImageUrl } from '../../utils/imageUtils';

const { width } = Dimensions.get('window');

// Helper for shadows (assuming it's defined elsewhere or needs to be added)
const getShadow = (level) => {
    switch (level) {
        case 'xs':
            return {
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 1 },
                shadowOpacity: 0.05,
                shadowRadius: 1,
                elevation: 1,
            };
        case 'md':
            return {
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.1,
                shadowRadius: 4,
                elevation: 3,
            };
        default:
            return {};
    }
};

const ProductDetailScreen = ({ route, navigation }) => {
    const { product } = route.params;
    const { addToCart } = useCart();
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [quantity, setQuantity] = useState(1);
    const [activeTab, setActiveTab] = useState('details'); // details, reviews

    const reviews = product.reviews || [];

    const images = (() => {
        if (!product.images) return ['https://via.placeholder.com/400'];
        try {
            const parsed = typeof product.images === 'string' ? JSON.parse(product.images) : product.images;
            const arr = Array.isArray(parsed) ? parsed : [parsed];
            return arr.length > 0 ? arr : ['https://via.placeholder.com/400'];
        } catch (e) {
            return [product.images];
        }
    })();

    const handleAddToCart = () => {
        addToCart({ ...product, quantity });
        // Minimal feedback
    };

    const renderHeader = () => (
        <View style={styles.header}>
            <TouchableOpacity
                style={styles.backButton}
                onPress={() => navigation.goBack()}
            >
                <Ionicons name="chevron-back" size={18} color="#1F2937" />
            </TouchableOpacity>
            <TouchableOpacity style={styles.headerIcon}>
                <Ionicons name="share-outline" size={18} color="#1F2937" />
            </TouchableOpacity>
        </View>
    );

    const renderImageGallery = () => (
        <View style={styles.galleryContainer}>
            <ScrollView
                horizontal
                pagingEnabled
                showsHorizontalScrollIndicator={false}
                onMomentumScrollEnd={(e) => {
                    const newIndex = Math.round(e.nativeEvent.contentOffset.x / width);
                    setSelectedImageIndex(newIndex);
                }}
            >
                {images.map((img, index) => (
                    <Image
                        key={index}
                        source={{ uri: getImageUrl(img) }}
                        style={styles.mainImage}
                    />
                ))}
            </ScrollView>
            <View style={styles.pagination}>
                {images.map((_, index) => (
                    <View
                        key={index}
                        style={[
                            styles.paginationDot,
                            index === selectedImageIndex && styles.paginationDotActive
                        ]}
                    />
                ))}
            </View>
        </View>
    );

    const renderDetails = () => (
        <View style={styles.contentSection}>
            <Text style={styles.descriptionTitle}>Product Overview</Text>
            <Text style={styles.descriptionText}>
                {product.description || 'Welcome to this product page. This item is carefully selected for our marketplace to ensure quality and value.'}
            </Text>

            <Text style={[styles.descriptionTitle, { marginTop: 15 }]}>Details</Text>
            <View style={styles.specRow}>
                <Text style={styles.specLabel}>Category</Text>
                <Text style={styles.specValue}>{product.category || 'General'}</Text>
            </View>
            <View style={styles.specRow}>
                <Text style={styles.specLabel}>Unit</Text>
                <Text style={styles.specValue}>{product.unit || 'Piece'}</Text>
            </View>
            <View style={styles.specRow}>
                <Text style={styles.specLabel}>Availability</Text>
                <Text style={[styles.specValue, { color: '#10B981' }]}>In Stock</Text>
            </View>
        </View>
    );

    const renderReviews = () => (
        <View style={styles.contentSection}>
            <View style={styles.reviewHeader}>
                <Text style={styles.reviewTitle}>Reviews ({reviews.length})</Text>
                <TouchableOpacity>
                    <Text style={styles.writeReviewText}>Write One</Text>
                </TouchableOpacity>
            </View>

            {reviews.length > 0 ? (
                reviews.map((review, index) => (
                    <View key={review.id || index} style={styles.reviewItem}>
                        <View style={styles.reviewTop}>
                            <Text style={styles.reviewUser}>{review.user || review.user_name}</Text>
                            <Text style={styles.reviewDate}>{review.date ? new Date(review.date).toLocaleDateString() : 'Today'}</Text>
                        </View>
                        <View style={styles.starRow}>
                            {[1, 2, 3, 4, 5].map((s) => (
                                <Ionicons
                                    key={s}
                                    name={s <= (review.rating || 5) ? "star" : "star-outline"}
                                    size={10}
                                    color="#F59E0B"
                                />
                            ))}
                        </View>
                        <Text style={styles.reviewText}>{review.text || review.comment}</Text>
                    </View>
                ))
            ) : (
                <View style={styles.noReviewsBox}>
                    <Ionicons name="chatbox-ellipses-outline" size={24} color="#CBD5E1" />
                    <Text style={styles.noReviewsText}>No feedback yet.</Text>
                </View>
            )}
        </View>
    );

    return (
        <View style={styles.container}>
            {renderHeader()}

            <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom: 20 }}>
                {renderImageGallery()}

                <View style={styles.mainInfoContainer}>
                    <View style={{ flex: 1 }}>
                        <Text style={styles.productName}>{product.name}</Text>
                        <View style={styles.ratingRow}>
                            <Ionicons name="star" size={12} color="#F59E0B" />
                            <Text style={styles.ratingValue}>{reviews.length > 0 ? (reviews.reduce((acc, r) => acc + (r.rating || 5), 0) / reviews.length).toFixed(1) : '5.0'}</Text>
                            <Text style={styles.ratingCount}>({reviews.length} reviews)</Text>
                        </View>
                    </View>
                    <View style={{ alignItems: 'flex-end' }}>
                        <Text style={styles.productPrice}>
                            <Text style={styles.currency}>KES </Text>
                            {Number(product.price).toLocaleString()}
                        </Text>
                    </View>
                </View>

                {/* Compact Tabs */}
                <View style={styles.tabContainer}>
                    <TouchableOpacity
                        style={[styles.tab, activeTab === 'details' && styles.activeTab]}
                        onPress={() => setActiveTab('details')}
                    >
                        <Text style={[styles.tabText, activeTab === 'details' && styles.activeTabText]}>Details</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                        style={[styles.tab, activeTab === 'reviews' && styles.activeTab]}
                        onPress={() => setActiveTab('reviews')}
                    >
                        <Text style={[styles.tabText, activeTab === 'reviews' && styles.activeTabText]}>Reviews</Text>
                    </TouchableOpacity>
                </View>

                {activeTab === 'details' ? renderDetails() : renderReviews()}
            </ScrollView>

            {/* Compact Bottom Action Bar */}
            <View style={styles.bottomBarContainer}>
                <View style={styles.actionBar}>
                    <View style={styles.quantityControl}>
                        <TouchableOpacity
                            style={styles.qtyBtn}
                            onPress={() => setQuantity(Math.max(1, quantity - 1))}
                        >
                            <Ionicons name="remove" size={14} color="#1F2937" />
                        </TouchableOpacity>
                        <Text style={styles.qtyText}>{quantity}</Text>
                        <TouchableOpacity
                            style={styles.qtyBtn}
                            onPress={() => setQuantity(quantity + 1)}
                        >
                            <Ionicons name="add" size={14} color="#1F2937" />
                        </TouchableOpacity>
                    </View>

                    <TouchableOpacity style={styles.addToCartBtn} onPress={handleAddToCart}>
                        <Ionicons name="cart-outline" size={16} color="#FFF" />
                        <Text style={styles.addToCartText}>Add to Cart</Text>
                    </TouchableOpacity>
                </View>
                <MarketplaceFooter activeTab="Home" navigation={navigation} />
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#F9FAFB', // Uniform off-white background
        paddingTop: Platform.OS === 'android' ? 40 : 0
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingHorizontal: 16,
        paddingVertical: 10,
        alignItems: 'center',
        zIndex: 10,
    },
    backButton: {
        width: 32,
        height: 32,
        backgroundColor: '#FFFFFF',
        borderRadius: 10,
        alignItems: 'center',
        justifyContent: 'center',
        ...getShadow('xs'),
    },
    headerIcon: {
        width: 32,
        height: 32,
        backgroundColor: '#FFFFFF',
        borderRadius: 10,
        alignItems: 'center',
        justifyContent: 'center',
        ...getShadow('xs'),
    },
    galleryContainer: {
        height: 220,
        width: '100%',
        backgroundColor: '#FFF',
        marginBottom: 16,
    },
    mainImage: {
        width: width,
        height: 220,
        resizeMode: 'contain',
    },
    pagination: {
        flexDirection: 'row',
        justifyContent: 'center',
        position: 'absolute',
        bottom: 10,
        width: '100%',
    },
    paginationDot: {
        width: 5,
        height: 5,
        borderRadius: 2.5,
        backgroundColor: '#E5E7EB',
        marginHorizontal: 3,
    },
    paginationDotActive: {
        backgroundColor: '#F59E0B',
        width: 12,
    },
    mainInfoContainer: {
        paddingHorizontal: 20,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
    },
    productName: {
        fontSize: 17,
        fontWeight: '800',
        color: '#111827',
        marginBottom: 2,
    },
    ratingRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    ratingValue: {
        fontSize: 11,
        fontWeight: '700',
        color: '#111827',
        marginLeft: 3,
    },
    ratingCount: {
        fontSize: 11,
        color: '#64748B',
        marginLeft: 3,
    },
    productPrice: {
        fontSize: 19,
        fontWeight: '900',
        color: '#F59E0B',
    },
    currency: {
        fontSize: 12,
        fontWeight: '700',
        color: '#94A3B8',
    },
    tabContainer: {
        flexDirection: 'row',
        paddingHorizontal: 20,
        marginBottom: 12,
        borderBottomWidth: 1,
        borderBottomColor: '#F1F5F9',
    },
    tab: {
        marginRight: 20,
        paddingBottom: 6,
    },
    activeTab: {
        borderBottomWidth: 2,
        borderBottomColor: '#F59E0B',
    },
    tabText: {
        fontSize: 13,
        color: '#94A3B8',
        fontWeight: '600',
    },
    activeTabText: {
        color: '#F59E0B',
        fontWeight: '800',
    },
    contentSection: {
        paddingHorizontal: 20,
    },
    descriptionTitle: {
        fontSize: 14,
        fontWeight: '800',
        color: '#111827',
        marginBottom: 6,
    },
    descriptionText: {
        fontSize: 12,
        lineHeight: 16,
        color: '#475569',
    },
    specRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingVertical: 8,
        borderBottomWidth: 1,
        borderBottomColor: '#F8FAFB',
    },
    specLabel: {
        fontSize: 12,
        color: '#64748B',
    },
    specValue: {
        fontSize: 12,
        fontWeight: '700',
        color: '#1E293B',
    },
    reviewHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 10,
    },
    reviewTitle: {
        fontSize: 14,
        fontWeight: '800',
        color: '#111827',
    },
    writeReviewText: {
        color: '#F59E0B',
        fontWeight: '800',
        fontSize: 11,
    },
    reviewItem: {
        marginBottom: 10,
        padding: 10,
        backgroundColor: '#FFFFFF',
        borderRadius: 12,
        borderWidth: 1,
        borderColor: '#F1F5F9',
    },
    reviewTop: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 2,
    },
    reviewUser: {
        fontSize: 12,
        fontWeight: '700',
        color: '#1E293B',
    },
    reviewDate: {
        fontSize: 10,
        color: '#94A3B8',
    },
    starRow: {
        flexDirection: 'row',
        marginBottom: 4,
    },
    reviewText: {
        fontSize: 11,
        color: '#475569',
        lineHeight: 14,
    },
    bottomBarContainer: {
        backgroundColor: '#FFFFFF',
        borderTopWidth: 1,
        borderTopColor: '#F1F5F9',
    },
    actionBar: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingVertical: 10,
        gap: 12,
    },
    quantityControl: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#F8FAFB',
        borderRadius: 12,
        padding: 2,
        borderWidth: 1,
        borderColor: '#F1F5F9',
    },
    qtyBtn: {
        width: 30,
        height: 30,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#FFF',
        borderRadius: 10,
        ...getShadow('xs'),
    },
    qtyText: {
        width: 28,
        textAlign: 'center',
        fontSize: 13,
        fontWeight: '800',
        color: '#111827',
    },
    addToCartBtn: {
        flex: 1,
        backgroundColor: '#111827',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        height: 40,
        borderRadius: 12,
        gap: 6,
        ...getShadow('md'),
    },
    addToCartText: {
        color: '#FFF',
        fontSize: 13,
        fontWeight: '800',
    },
    noReviewsBox: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 30,
        backgroundColor: '#FFFFFF',
        borderRadius: 16,
        marginTop: 10,
        borderWidth: 1,
        borderColor: '#F1F5F9',
    },
    noReviewsText: {
        fontSize: 12,
        fontWeight: '700',
        color: '#64748B',
        marginTop: 8,
    },
});

export default ProductDetailScreen;
